/* =========================================================================
 * MineMap.tsx — the Australia map, and its locator-map variant.
 *
 * WHAT THIS IS
 *   A pure presentational SVG map: ocean, continental shelf, the eight state
 *   polygons, the eight capital dots + labels, and one pin per mine site with
 *   its machine count and its leader-lined site name. The caller decides what
 *   colour each pin is (it owns the worst-band computation); this component
 *   only draws what `pins` says.
 *
 * PORTED FROM
 *   /Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html
 *     lines 2037-2052  mapSVG()  -> <MineMap />
 *     lines 2077-2081  miniMap() -> <MineMap mini />
 *   Geometry (STATES / CAPS / proj / MAPVB / MLBL, demo lines 2021-2026)
 *   lives in ./au-geography — see that file's header for why the polygons
 *   are baked artwork rather than a geodata import.
 *
 * ZOOM, PAN AND LABEL DRAGGING (`interactive`, demo lines 2027-2075)
 *   Opt-in with the `interactive` prop; the default render has none of it.
 *   - Zoom. `mapZoom` / `mapApply`: wheel, double-click and the `.mapctl`
 *     toolbar's − / + buttons, clamped to 70 %-600 %, anchored on the pointer
 *     (or the frame centre for the buttons), with the percentage readout.
 *   - Pan. The pointer drag on the empty map, in viewBox units.
 *   - Label dragging. `MLPOS` / `mlPaint`: a site name drags anywhere, its
 *     leader line re-trimmed back to the pin exactly as `mlPaint` does it.
 *   - Labels counter-scale by `1 / k^0.72` so they stay legible as the map
 *     grows — the demo's own constant.
 *   - "Reset view" and "Reset labels", as in the demo.
 *   All of it is React state: nothing persists, and a remount starts at rest.
 *
 *   The viewBox never changes. Zoom and pan are one `translate() scale()`
 *   group round the drawing, and that group only exists once the view has
 *   moved — so **at rest the `<svg>` is byte-identical to the non-interactive
 *   render**, and a default label keeps mapSVG()'s own leader endpoint
 *   `(dx, dy - 3)` until it is dragged. Cursors live on the wrapper, not the
 *   svg, for the same reason (`cursor` inherits).
 *
 * DECLARED OMISSIONS (relative to the demo)
 *   - The label tooltip "Drag to move this label". The toolbar's hint line
 *     carries the same instruction once, rather than on six labels.
 *   - The demo's state `<title>` also listed that state's sites and machine
 *     count. A `MineMapPin` carries no state code, and this component is pure
 *     over `pins`, so the state tooltip is the state name alone.
 *   - The minimap's pulsing `@keyframes mglow` on the selected dot's glow: it
 *     lived in the demo's stylesheet, which this port does not own. The glow
 *     is drawn, it simply does not animate.
 *
 * COLOUR
 *   No hex anywhere. Map chrome reads design tokens confirmed in
 *   src/versions/v1/styles/tokens.css, applied inline because this port does
 *   not own a stylesheet. Pin fills come from the caller's `colour` prop
 *   (a `var(--band-*)` token). Every token below has a light and a dark
 *   value, which is what keeps the map legible in both themes.
 *
 * DETERMINISM
 *   Every emitted coordinate goes through `.toFixed(1)`, as the demo does for
 *   its polygons and capital dots, so two renders of the same `pins` are
 *   byte-identical. No Date.now(), no Math.random(), no module-level counter.
 *   (The demo emitted raw floats for the pin circles specifically; rounding
 *   those too is the one intentional tightening, worth ~0.05px.)
 * ========================================================================= */

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/shared/cn';
import { BUTTON_GHOST } from '@/shared/ui-class';
import type { MineCode } from '../../schema/machine';
import {
  AU_CAPITALS,
  AU_STATES,
  MAP_VIEWBOX,
  MINE_LABEL_OFFSET,
  polygonPoints,
  project,
} from './au-geography';

// ── Tokens ────────────────────────────────────────────────────────────────
// Confirmed in src/versions/v1/styles/tokens.css (light / dark pairs).
// The three neutral surfaces sit within a couple of percent of each other in
// light mode, so a straight `--surface-base` ocean left the continent invisible
// against it. The ocean and the shelf therefore carry a light wash of the
// informational blue over the surface token: enough separation to read the
// coastline in both themes, still a theme colour rather than a literal.
const OCEAN = 'color-mix(in srgb, var(--state-info) 7%, var(--surface-base))'; // tokens.css:10 / :51 + :27 / :75
const SHELF = 'color-mix(in srgb, var(--state-info) 14%, var(--surface-base))'; // shallow-water halo
const LAND = 'var(--surface-2)';          // tokens.css:12 / :53
const LAND_LINE = 'var(--hairline-strong)'; // tokens.css:30 / :78
const MUTED_INK = 'var(--ink-tertiary)';  // tokens.css:17 / :61 — capitals, leader lines, idle dots
const LABEL_INK = 'var(--ink-primary)';   // tokens.css:15 / :59 — site names
const PIN_RING = 'var(--surface-2)';      // tokens.css:12 / :53 — the demo's white cut-out ring
const PIN_TEXT = 'var(--accent-text)';    // tokens.css:22 / :70 — sits on the saturated pin fill
const FOCUS_RING = 'var(--accent)';       // tokens.css:19 / :67

const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** SVG user units, 1 dp — the rounding that makes renders byte-stable. */
const u = (n: number): string => n.toFixed(1);

export interface MineMapPin {
  readonly mine: MineCode;
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
  readonly machines: number;
  /** CSS colour for the pin — the caller passes a `var(--band-*)` token. */
  readonly colour: string;
  /** Extra line for the hover tooltip, e.g. '14 machines · 2 very poor or worse'. */
  readonly detail: string;
}

export interface MineMapProps {
  pins: readonly MineMapPin[];
  /** Highlighted pin, or null. */
  selected?: MineCode | null;
  onSelect?: (mine: MineCode) => void;
  /** Locator variant — smaller, no capitals, no site names. Default false. */
  mini?: boolean;
  /** CSS pixel width for the mini variant, e.g. 150. Ignored when `mini` is false. */
  width?: number;
  /**
   * Zoom, pan and draggable site names, with the demo's control bar above the
   * map. Default false. Ignored by the `mini` variant.
   */
  interactive?: boolean;
  className?: string;
}

// ── Zoom and pan (demo lines 2028-2075) ───────────────────────────────────

/** Demo line 2028. `MAPZ` — scale and translation, in viewBox units. */
interface MapView {
  readonly k: number;
  readonly x: number;
  readonly y: number;
}

const REST: MapView = { k: 1, x: 0, y: 0 };

/** Demo line 2056. Zoom is clamped to 70 %-600 %. */
const ZOOM_MIN = 0.7;
const ZOOM_MAX = 6;
/** Demo line 2052 (buttons), 2061 (wheel notch), 2075 (double-click). */
const ZOOM_STEP_BUTTON = 1.35;
const ZOOM_STEP_WHEEL = 1.16;
const ZOOM_STEP_DBLCLICK = 1.6;

const atRest = (v: MapView): boolean => v.k === 1 && v.x === 0 && v.y === 0;

/**
 * Demo line 2054. Labels counter-scale far more slowly than the map
 * (`1 / k^0.72`), so they stay legible at any zoom without swamping the map.
 */
const labelScale = (k: number): number => 1 / Math.pow(k, 0.72);

/**
 * Demo lines 2056-2058. `mapZoom` — scale by `factor` about a point, which is
 * the pointer when there is one and the frame centre otherwise, so the spot
 * under the cursor stays put.
 */
function zoomAbout(
  v: MapView,
  factor: number,
  rect: DOMRect | null,
  clientX?: number,
  clientY?: number,
): MapView {
  const [vx, vy, vw, vh] = MAP_VIEWBOX;
  const k2 = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.k * factor));
  const px =
    clientX == null || !rect ? vx + vw / 2 : vx + ((clientX - rect.left) / rect.width) * vw;
  const py =
    clientY == null || !rect ? vy + vh / 2 : vy + ((clientY - rect.top) / rect.height) * vh;
  return { k: k2, x: px - (px - v.x) * (k2 / v.k), y: py - (py - v.y) * (k2 / v.k) };
}

/**
 * Demo lines 2033-2036. `mlPaint`'s leader: the line stops short of a dragged
 * label by up to 11 units, so it points at the name rather than running
 * through it.
 */
function trimmedLeader(dx: number, dy: number): readonly [number, number] {
  const r = Math.hypot(dx, dy) || 1;
  const gap = Math.min(11, r * 0.45);
  return [dx - (dx / r) * gap, dy - 3 - ((dy - 3) / r) * gap * 0.5];
}

/** A drag in progress — a pan of the map, or one site name. */
type Drag =
  | { readonly kind: 'pan'; readonly sx: number; readonly sy: number; readonly from: MapView }
  | {
      readonly kind: 'label';
      readonly mine: MineCode;
      readonly sx: number;
      readonly sy: number;
      readonly ox: number;
      readonly oy: number;
      /** Client pixels → label units: viewBox scale ÷ zoom ÷ label counter-scale. */
      readonly f: number;
    };

type TextAnchor = 'start' | 'middle' | 'end';

/**
 * `MINE_LABEL_OFFSET` types its anchor as `string` (the contract's shape), so
 * narrow it back to the SVG union at the point of use rather than casting.
 */
function asAnchor(value: string): TextAnchor {
  return value === 'start' || value === 'end' ? value : 'middle';
}

/** Enter / Space activate a `role="button"` group, as the platform expects. */
function isActivationKey(event: ReactKeyboardEvent<SVGGElement>): boolean {
  return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
}

interface PinShapeProps {
  pin: MineMapPin;
  selected: boolean;
  onSelect?: (mine: MineCode) => void;
}

/**
 * Demo line 2049 — the `.pin` group: halo, disc, machine count, `<title>`.
 * The demo drove hover off CSS (`.pin:hover .halo{opacity:.38}`) and the
 * selected state off a `.sel` class; here both are React state on the group
 * so no stylesheet is needed, and focus gets the same affordance as hover.
 */
function PinShape({ pin, selected, onSelect }: PinShapeProps) {
  const [active, setActive] = useState(false);
  const [x, y] = project([pin.lon, pin.lat]);
  const lit = active || selected;

  const activate = () => onSelect?.(pin.mine);

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`${pin.name} — ${pin.detail}`}
      style={{ cursor: onSelect ? 'pointer' : 'default', outline: 'none' }}
      onClick={activate}
      onKeyDown={(event) => {
        if (!isActivationKey(event)) return;
        event.preventDefault();
        activate();
      }}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      <title>{`${pin.name} · ${pin.detail}`}</title>
      <circle cx={u(x)} cy={u(y)} r={22} fill={pin.colour} opacity={lit ? 0.38 : 0.15} />
      {lit ? (
        <circle
          cx={u(x)}
          cy={u(y)}
          r={14}
          fill="none"
          stroke={FOCUS_RING}
          strokeWidth={1.6}
          opacity={0.85}
        />
      ) : null}
      <circle
        cx={u(x)}
        cy={u(y)}
        r={lit ? 10.5 : 9}
        fill={pin.colour}
        stroke={PIN_RING}
        strokeWidth={2}
      />
      <text
        x={u(x)}
        y={u(y + 3.5)}
        textAnchor="middle"
        fontSize={9}
        fontWeight={700}
        fill={PIN_TEXT}
        style={{ fontFamily: MONO, pointerEvents: 'none' }}
      >
        {pin.machines}
      </text>
    </g>
  );
}

interface MiniDotProps {
  pin: MineMapPin;
  selected: boolean;
  onSelect?: (mine: MineCode) => void;
}

/** Demo lines 2079-2080 — the locator map's `.mdot`, lit when it is `sel`. */
function MiniDot({ pin, selected, onSelect }: MiniDotProps) {
  const [active, setActive] = useState(false);
  const [x, y] = project([pin.lon, pin.lat]);
  const lit = active || selected;

  const activate = () => onSelect?.(pin.mine);

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={pin.name}
      style={{ cursor: onSelect ? 'pointer' : 'default', outline: 'none' }}
      onClick={activate}
      onKeyDown={(event) => {
        if (!isActivationKey(event)) return;
        event.preventDefault();
        activate();
      }}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      <title>{pin.name}</title>
      {selected ? (
        <circle cx={u(x)} cy={u(y)} r={19} fill={FOCUS_RING} opacity={0.22} />
      ) : null}
      <circle cx={u(x)} cy={u(y)} r={selected ? 11 : 6.5} fill={lit ? FOCUS_RING : MUTED_INK} />
    </g>
  );
}

/**
 * The map. `mini` swaps in the demo's locator variant: states + one dot per
 * site, no ocean wash, no capitals, no site names, sized in CSS pixels.
 */
export function MineMap({
  pins,
  selected = null,
  onSelect,
  mini = false,
  width = 200,
  interactive = false,
  className,
}: MineMapProps): JSX.Element {
  const viewBox = MAP_VIEWBOX.join(' ');
  const states = Object.entries(AU_STATES);

  // Zoom, pan and dragged label positions. Declared before the `mini` return
  // so the hook order never changes; the mini variant simply ignores them.
  const [view, setView] = useState<MapView>(REST);
  const [labelPos, setLabelPos] = useState<Partial<Record<MineCode, readonly [number, number]>>>(
    {},
  );
  const [dragging, setDragging] = useState<Drag['kind'] | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<Drag | null>(null);
  const live = interactive && !mini;

  // Demo line 2061. The wheel must be a native, non-passive listener: React
  // attaches `onWheel` passively, and a passive listener cannot stop the page
  // scrolling underneath the zoom.
  useEffect(() => {
    const svg = svgRef.current;
    if (!live || !svg) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = svg.getBoundingClientRect();
      const factor = event.deltaY < 0 ? ZOOM_STEP_WHEEL : 1 / ZOOM_STEP_WHEEL;
      setView((v) => zoomAbout(v, factor, rect, event.clientX, event.clientY));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [live]);

  if (mini) {
    return (
      <svg
        className={className}
        viewBox={viewBox}
        role="img"
        aria-label="Locator map"
        style={{ width: `${width}px`, height: 'auto', display: 'block' }}
      >
        {states.map(([code, state]) => (
          <polygon
            key={`mini-${code}`}
            points={polygonPoints(state.p)}
            fill={LAND}
            stroke={LAND_LINE}
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        ))}
        {pins.map((pin) => (
          <MiniDot
            key={`mini-dot-${pin.mine}`}
            pin={pin}
            selected={pin.mine === selected}
            onSelect={onSelect}
          />
        ))}
      </svg>
    );
  }

  // Demo line 2039 — an ocean rect deliberately far larger than the viewBox,
  // so the sea still covers the frame when the map is inset or over-scrolled.
  const oceanRect = {
    x: MAP_VIEWBOX[0] - 600,
    y: MAP_VIEWBOX[1] - 600,
    width: MAP_VIEWBOX[2] + 1200,
    height: MAP_VIEWBOX[3] + 1200,
  };

  // Demo line 2055 — the counter-scale every label group carries once zoomed.
  // Empty at 100 %, which keeps the at-rest markup identical.
  const labelSuffix = view.k === 1 ? '' : ` scale(${labelScale(view.k).toFixed(3)})`;

  const svgRect = (): DOMRect | null => svgRef.current?.getBoundingClientRect() ?? null;

  // Demo lines 2064-2067 — press on a site name to drag it.
  const startLabelDrag = (mine: MineCode, dx: number, dy: number) =>
    (event: ReactPointerEvent<SVGGElement>) => {
      if (event.button !== 0) return;
      const rect = svgRect();
      if (!rect) return;
      event.stopPropagation();
      event.preventDefault();
      drag.current = {
        kind: 'label',
        mine,
        sx: event.clientX,
        sy: event.clientY,
        ox: dx,
        oy: dy,
        f: MAP_VIEWBOX[2] / rect.width / view.k / labelScale(view.k),
      };
      svgRef.current?.setPointerCapture(event.pointerId);
      setDragging('label');
    };

  // Demo line 2072 — press on the map itself to pan. A press on a pin is left
  // alone (the demo's `closest('.pin')` test): capturing it would retarget the
  // release to the svg and the pin's click would never fire. Pins are the only
  // `role="button"` inside the map, so no extra attribute is needed to find them.
  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    if ((event.target as Element).closest('[role="button"]')) return;
    drag.current = { kind: 'pan', sx: event.clientX, sy: event.clientY, from: view };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging('pan');
  };

  // Demo lines 2068-2069 and 2073.
  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    if (d.kind === 'label') {
      const nx = d.ox + (event.clientX - d.sx) * d.f;
      const ny = d.oy + (event.clientY - d.sy) * d.f;
      setLabelPos((p) => ({ ...p, [d.mine]: [nx, ny] as const }));
      return;
    }
    const rect = svgRect();
    if (!rect) return;
    setView({
      k: d.from.k,
      x: d.from.x + ((event.clientX - d.sx) / rect.width) * MAP_VIEWBOX[2],
      y: d.from.y + ((event.clientY - d.sy) / rect.height) * MAP_VIEWBOX[3],
    });
  };

  const endDrag = () => {
    drag.current = null;
    setDragging(null);
  };

  const drawing = (
    <>
      <rect
        x={u(oceanRect.x)}
        y={u(oceanRect.y)}
        width={u(oceanRect.width)}
        height={u(oceanRect.height)}
        fill={OCEAN}
      />

      {/* Demo line 2040 — the shelf: the same rings, fattened by a 9-unit
          stroke, so the coast gets a shallow-water halo rather than a hard
          edge against the ocean. */}
      {states.map(([code, state]) => (
        <polygon
          key={`shelf-${code}`}
          points={polygonPoints(state.p)}
          fill={SHELF}
          stroke={SHELF}
          strokeWidth={9}
          strokeLinejoin="round"
        />
      ))}

      {/* Demo lines 2041-2042 — the land itself. */}
      {states.map(([code, state]) => (
        <polygon
          key={`state-${code}`}
          points={polygonPoints(state.p)}
          fill={LAND}
          stroke={LAND_LINE}
          strokeWidth={1.2}
          strokeLinejoin="round"
        >
          <title>{state.n}</title>
        </polygon>
      ))}

      {/* Demo lines 2043-2044 — capital dots, each with its hand-placed label.
          The label sits in its own translated group, which is where the zoom
          applies its counter-scale. */}
      {AU_CAPITALS.map(([name, lon, lat, anchor, dx, dy]) => {
        const [x, y] = project([lon, lat]);
        return (
          <g key={`cap-${name}`}>
            <circle cx={u(x)} cy={u(y)} r={2.6} fill={MUTED_INK} />
            <g transform={`translate(${u(x)} ${u(y)})${labelSuffix}`}>
              <text
                x={dx}
                y={dy}
                textAnchor={anchor}
                fontSize={9.5}
                fill={MUTED_INK}
                stroke={OCEAN}
                strokeWidth={2.6}
                paintOrder="stroke"
              >
                {name}
              </text>
            </g>
          </g>
        );
      })}

      {/* Demo line 2049 — pins, drawn before the labels so no pin covers a
          site name. */}
      {pins.map((pin) => (
        <PinShape
          key={`pin-${pin.mine}`}
          pin={pin}
          selected={pin.mine === selected}
          onSelect={onSelect}
        />
      ))}

      {/* Demo line 2050 — site names on top, each on a leader line back to
          its pin. At the MINE_LABEL_OFFSET default the leader ends at
          mapSVG()'s `(dx, dy - 3)`; once dragged it is trimmed as mlPaint()
          trims it. */}
      {pins.map((pin) => {
        const [x, y] = project([pin.lon, pin.lat]);
        const offset = MINE_LABEL_OFFSET[pin.mine] ?? ([0, -17, 0, 'middle'] as const);
        const [defaultDx, defaultDy, , anchor] = offset;
        const moved = labelPos[pin.mine];
        const [dx, dy] = moved ?? [defaultDx, defaultDy];
        const [lx, ly] = moved ? trimmedLeader(dx, dy) : [dx, dy - 3];
        return (
          <g
            key={`label-${pin.mine}`}
            transform={`translate(${u(x)} ${u(y)})${labelSuffix}`}
            onPointerDown={live ? startLabelDrag(pin.mine, dx, dy) : undefined}
          >
            <line
              x1={0}
              y1={0}
              x2={u(lx)}
              y2={u(ly)}
              stroke={MUTED_INK}
              strokeWidth={1.1}
              strokeDasharray="3 2.5"
              opacity={0.6}
            />
            <text
              x={u(dx)}
              y={u(dy)}
              textAnchor={asAnchor(anchor)}
              fontSize={11.5}
              fontWeight={600}
              fill={LABEL_INK}
              stroke={OCEAN}
              strokeWidth={3.5}
              paintOrder="stroke"
            >
              {pin.name}
            </text>
          </g>
        );
      })}
    </>
  );

  const map = (
    <svg
      ref={svgRef}
      className={className}
      viewBox={viewBox}
      role="img"
      aria-label="Map of Australia with the six mine sites"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onPointerDown={live ? onPointerDown : undefined}
      onPointerMove={live ? onPointerMove : undefined}
      onPointerUp={live ? endDrag : undefined}
      onPointerCancel={live ? endDrag : undefined}
      onDoubleClick={
        live
          ? (event) =>
              setView((v) =>
                zoomAbout(v, ZOOM_STEP_DBLCLICK, svgRect(), event.clientX, event.clientY),
              )
          : undefined
      }
    >
      {/* Demo line 2038 — `#map-g`. Only drawn once the view has moved, so the
          markup at rest is the plain drawing. */}
      {atRest(view) ? (
        drawing
      ) : (
        <g transform={`translate(${u(view.x)} ${u(view.y)}) scale(${view.k.toFixed(3)})`}>
          {drawing}
        </g>
      )}
    </svg>
  );

  if (!live) return map;

  const moved = Object.keys(labelPos).length > 0;

  return (
    <div className={className}>
      <MapControls
        zoomPct={Math.round(view.k * 100)}
        canZoomIn={view.k < ZOOM_MAX}
        canZoomOut={view.k > ZOOM_MIN}
        onZoom={(factor) => setView((v) => zoomAbout(v, factor, svgRect()))}
        viewMoved={!atRest(view)}
        onResetView={() => setView(REST)}
        labelsMoved={moved}
        onResetLabels={() => setLabelPos({})}
      />
      {/* `cursor` inherits into the svg, so setting it here leaves the svg
          markup untouched. Pins set their own pointer cursor. At 100 % the page
          still scrolls vertically over the map on touch; once zoomed, touch
          belongs to the map. */}
      <div
        style={{
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: atRest(view) ? 'pan-y' : 'none',
          overflow: 'hidden',
          borderRadius: 12,
        }}
      >
        {map}
      </div>
    </div>
  );
}

interface MapControlsProps {
  zoomPct: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoom: (factor: number) => void;
  viewMoved: boolean;
  onResetView: () => void;
  labelsMoved: boolean;
  onResetLabels: () => void;
}

/**
 * Demo line 2052. The `.mapctl` bar: − / readout / +, the two resets, and the
 * one-line hint. Real buttons, so zoom and both resets work from the keyboard;
 * the readout is a polite live region so a screen reader hears the new zoom.
 */
function MapControls({
  zoomPct,
  canZoomIn,
  canZoomOut,
  onZoom,
  viewMoved,
  onResetView,
  labelsMoved,
  onResetLabels,
}: MapControlsProps): JSX.Element {
  const small = cn(BUTTON_GHOST, 'min-w-9 px-3 text-xs');
  return (
    <div role="group" aria-label="Map view" className="mb-2 flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        className={small}
        onClick={() => onZoom(1 / ZOOM_STEP_BUTTON)}
        disabled={!canZoomOut}
        aria-label="Zoom out"
        title="Zoom out"
      >
        −
      </button>
      <span className="mono min-w-[3.25rem] text-center text-xs text-muted" aria-live="polite">
        {zoomPct}%
      </span>
      <button
        type="button"
        className={small}
        onClick={() => onZoom(ZOOM_STEP_BUTTON)}
        disabled={!canZoomIn}
        aria-label="Zoom in"
        title="Zoom in"
      >
        +
      </button>
      <button type="button" className={small} onClick={onResetView} disabled={!viewMoved}>
        Reset view
      </button>
      <button type="button" className={small} onClick={onResetLabels} disabled={!labelsMoved}>
        Reset labels
      </button>
      <span className="w-full text-[11px] text-muted">
        scroll to zoom · drag to pan · drag a mine name to move it
      </span>
    </div>
  );
}
